import React from "react";
import * as Sentry from "@sentry/react";
import FaceLogo from "@/assets/static/Peerbots152.png";
import { ReactNode } from "react";
import { Button, Text, Heading, Anchor } from "@peerbots/core";
import { useTranslation, Trans } from "react-i18next";

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorInfo {
  componentStack: string;
}

interface GlobalErrorState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

function GlobalErrorView({
  error,
  errorInfo,
  showDetails,
  onToggleDetails,
}: {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
  onToggleDetails: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="m-4 p-4">
      <div className="inline-block mr-4">
        <img
          src={FaceLogo}
          alt={"Peerbots Logo"}
          className="block mx-auto py-1 px-2 aspect-auto w-32 rotate-180"
        />
      </div>
      <div className="inline-block">
        <Heading level={2} className="mb-4">
          {t("errors.globalErrorTitle")}
        </Heading>
        <Text>
          <Trans
            i18nKey="errors.globalErrorReport"
            components={{
              1: (
                <Anchor
                  href="https://forms.wix.com/f/7228812310930784350"
                  target="_blank"
                />
              ),
            }}
          />
        </Text>
        <Text>
          <Trans
            i18nKey="errors.globalErrorRefresh"
            components={{
              1: (
                <Anchor
                  onClick={() => {
                    location.reload();
                  }}
                />
              ),
            }}
          />
        </Text>
        <Text>
          <Trans
            i18nKey="errors.globalErrorReset"
            components={{
              1: (
                <Anchor
                  onClick={() => {
                    localStorage.clear();
                    location.reload();
                  }}
                />
              ),
            }}
          />
        </Text>
        <div className="mt-4">
          <Button variant="secondary" size="sm" onClick={onToggleDetails}>
            {showDetails ? t("errors.hideDetails") : t("errors.viewDetails")}
          </Button>
        </div>
        {showDetails && (
          <div className="mt-4 p-4 bg-gray-100 rounded text-xs font-mono overflow-auto max-h-96 max-w-2xl border border-gray-300">
            <Text variant="error" className="mb-2 font-bold">
              {error?.name}: {error?.message}
            </Text>
            <pre className="whitespace-pre-wrap">{error?.stack}</pre>
            {errorInfo && (
              <div className="mt-4 border-t border-gray-300 pt-2">
                <Text className="font-bold mb-1">
                  {t("errors.componentStack")}
                </Text>
                <pre className="whitespace-pre-wrap">
                  {errorInfo.componentStack}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default class GlobalError extends React.Component<
  ErrorBoundaryProps,
  GlobalErrorState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    Sentry.captureException(error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <GlobalErrorView
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          showDetails={this.state.showDetails}
          onToggleDetails={() =>
            this.setState((s) => ({ showDetails: !s.showDetails }))
          }
        />
      );
    }

    return this.props.children;
  }
}
